import { useEffect, useState, useMemo } from "react";
import { finAPI, fmtINR, fmtDate, METHOD_STYLE } from "./finApi";
import { Activity, Filter, RefreshCw, ChevronDown, ChevronRight, Copy } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { NumberInput } from "@/components/ui/number-input";

const KIND_STYLE = {
  "payment.received":       "bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/20",
  "payment.voided":         "bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/20",
  "refund.requested":       "bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/20",
  "refund.approved":        "bg-sky-500/15 text-sky-700 ring-1 ring-sky-500/20",
  "refund.rejected":        "bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/20",
  "refund.processed":       "bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/20",
  "discount.requested":     "bg-fuchsia-500/15 text-fuchsia-700 ring-1 ring-fuchsia-500/20",
  "discount.approved":      "bg-fuchsia-500/25 text-fuchsia-800 ring-1 ring-fuchsia-500/30",
  "discount.rejected":      "bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/20",
  "comp.granted":           "bg-violet-500/15 text-violet-700 ring-1 ring-violet-500/20",
  "comp.approved":          "bg-violet-500/25 text-violet-800 ring-1 ring-violet-500/30",
  "cash.shift_opened":      "bg-sky-500/15 text-sky-700 ring-1 ring-sky-500/20",
  "cash.txn_recorded":      "bg-slate-500/15 text-slate-700 ring-1 ring-slate-500/20",
  "cash.deposit":           "bg-cyan-500/15 text-cyan-700 ring-1 ring-cyan-500/20",
  "cash.shift_closed":      "bg-slate-500/25 text-slate-800 ring-1 ring-slate-500/30",
  "night_audit.completed":  "bg-hocp-navy text-white",
  "revenue.flag_created":   "bg-rose-500/25 text-rose-800 ring-1 ring-rose-500/30",
  "revenue.flag_resolved":  "bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/20",
};

function EventRow({ e }) {
  const [expanded, setExpanded] = useState(false);
  const kindCls = KIND_STYLE[e.kind] || "bg-slate-100 text-slate-700";
  return (
    <>
      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(!expanded)}
        data-testid={`fin-event-row-${e.id}`}>
        <td className="px-4 py-2.5 w-8">{expanded ? <ChevronDown className="w-4 h-4 text-slate-400"/> : <ChevronRight className="w-4 h-4 text-slate-400"/>}</td>
        <td className="px-4 py-2.5">
          <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium font-mono ${kindCls}`}>{e.kind}</span>
        </td>
        <td className="px-4 py-2.5 font-mono text-[12px] text-slate-500">{e.entity_type}</td>
        <td className="px-4 py-2.5 text-right font-medium tabular-nums text-hocp-navy">
          {e.amount != null ? fmtINR(e.amount) : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-2.5 text-slate-600 text-[12px]">{e.actor_email}</td>
        <td className="px-4 py-2.5 text-slate-600 tabular-nums text-[12px]">{e.business_date}</td>
        <td className="px-4 py-2.5 text-slate-500 text-[12px]">{fmtDate(e.at)}</td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/60">
          <td colSpan={7} className="px-8 py-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-[12px]">
              <Meta label="Event ID"       value={e.id} mono/>
              <Meta label="Entity ID"      value={e.entity_id} mono/>
              <Meta label="Module Source"  value={e.module_source}/>
              <Meta label="Actor ID"       value={e.actor_id} mono/>
              <Meta label="Currency"       value={e.currency}/>
              <Meta label="Previous State" value={e.previous_state || "—"}/>
              <Meta label="New State"      value={e.new_state || "—"}/>
              <Meta label="Business Date"  value={e.business_date}/>
            </div>
            {e.metadata && Object.keys(e.metadata).length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Metadata</div>
                <pre className="text-[11px] font-mono bg-white border border-slate-200 rounded-lg p-3 overflow-x-auto text-slate-700"
                  data-testid={`fin-event-metadata-${e.id}`}>{JSON.stringify(e.metadata, null, 2)}</pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Meta({ label, value, mono = false }) {
  const copy = () => { try { navigator.clipboard.writeText(String(value)); } catch (_) { /* noop */ } };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-slate-800 mt-0.5 flex items-center gap-1 ${mono ? "font-mono text-[11px]" : ""}`}>
        <span className="truncate">{value}</span>
        {mono && value && <button onClick={copy} className="opacity-40 hover:opacity-100"><Copy className="w-3 h-3"/></button>}
      </div>
    </div>
  );
}

export default function FinanceEvents() {
  const [items, setItems] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    kind: "", business_date: "", actor_id: "", module_source: "", entity_type: "",
    entity_id: "", payment_method: "", reservation_id: "", folio_id: "",
    min_amount: "", max_amount: "", limit: 200,
  });

  useEffect(() => { finAPI.eventKinds().then(setKinds).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
    finAPI.events(params).then(setItems).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const byKind = {};
    let total = 0;
    items.forEach(e => {
      byKind[e.kind] = (byKind[e.kind] || 0) + 1;
      if (e.amount != null) total += Number(e.amount);
    });
    return { count: items.length, byKind, total };
  }, [items]);

  const clear = () => {
    setFilters({
      kind: "", business_date: "", actor_id: "", module_source: "", entity_type: "",
      entity_id: "", payment_method: "", reservation_id: "", folio_id: "",
      min_amount: "", max_amount: "", limit: 200,
    });
    setTimeout(load, 0);
  };

  return (
    <div className="space-y-4" data-testid="fin-events-page">
      {/* Header */}
      <div className="on-dark bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-5 border border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
              <Activity className="w-3.5 h-3.5"/> Immutable Financial Event Ledger
            </div>
            <div className="mt-1 text-white text-[13px]">
              <span className="font-display text-[28px] font-semibold tabular-nums" data-testid="fin-events-count">{stats.count}</span>
              <span className="text-slate-300 ml-2 text-[13px]">events · amount total {fmtINR(stats.total)}</span>
            </div>
          </div>
          <button onClick={load} disabled={loading} data-testid="fin-events-refresh"
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[13px] font-semibold inline-flex items-center gap-2 shadow-sm">
            <RefreshCw className={`w-4 h-4 text-hocp-gold ${loading ? "animate-spin" : ""}`}/>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <Filter className="w-4 h-4"/> Filters
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <select value={filters.kind} onChange={e => setFilters({...filters, kind: e.target.value})}
            data-testid="fin-events-filter-kind"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
            <option value="">Any kind</option>
            {kinds.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <DatePicker value={(filters.business_date || '').slice(0,10)} onChange={(_v) => setFilters({...filters, business_date: _v})} data-testid="fin-events-filter-date"/>
          <select value={filters.payment_method} onChange={e => setFilters({...filters, payment_method: e.target.value})}
            data-testid="fin-events-filter-method"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
            <option value="">Any payment method</option>
            {Object.entries(METHOD_STYLE).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
          </select>
          <select value={filters.module_source} onChange={e => setFilters({...filters, module_source: e.target.value})}
            data-testid="fin-events-filter-module"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
            <option value="">Any module</option>
            <option value="finance">finance</option>
            <option value="fo">fo</option>
            <option value="pos">pos</option>
            <option value="pms">pms</option>
          </select>
          <input value={filters.folio_id} onChange={e => setFilters({...filters, folio_id: e.target.value})}
            placeholder="Folio ID" data-testid="fin-events-filter-folio"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          <input value={filters.reservation_id} onChange={e => setFilters({...filters, reservation_id: e.target.value})}
            placeholder="Reservation ID" data-testid="fin-events-filter-reservation"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          <input value={filters.actor_id} onChange={e => setFilters({...filters, actor_id: e.target.value})}
            placeholder="Actor / User ID" data-testid="fin-events-filter-actor"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          <input value={filters.entity_type} onChange={e => setFilters({...filters, entity_type: e.target.value})}
            placeholder="Entity type (fin_payment…)" data-testid="fin-events-filter-entity-type"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          <input value={filters.entity_id} onChange={e => setFilters({...filters, entity_id: e.target.value})}
            placeholder="Entity ID" data-testid="fin-events-filter-entity-id"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          <input value={filters.min_amount} onChange={e => setFilters({...filters, min_amount: e.target.value})}
            placeholder="Min ₹" data-testid="fin-events-filter-min"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          <input value={filters.max_amount} onChange={e => setFilters({...filters, max_amount: e.target.value})}
            placeholder="Max ₹" data-testid="fin-events-filter-max"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
          <NumberInput value={filters.limit} onChange={e => setFilters({...filters, limit: Number(e.target.value) || 200})}
            placeholder="Limit" data-testid="fin-events-filter-limit"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] font-mono"/>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={clear} data-testid="fin-events-clear"
            className="text-[12px] text-slate-500 hover:text-slate-700 px-3 py-1.5">Clear</button>
          <button onClick={load} data-testid="fin-events-apply"
            className="px-4 py-1.5 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium">Apply Filters</button>
        </div>
      </div>

      {/* Kind chips summary */}
      {Object.keys(stats.byKind).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.byKind).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
            <button key={k} onClick={() => { setFilters({...filters, kind: k}); setTimeout(load, 0); }}
              data-testid={`fin-events-chip-${k}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium font-mono ${KIND_STYLE[k] || "bg-slate-100 text-slate-700"}`}>
              {k} <span className="opacity-70">·</span> <span className="tabular-nums">{n}</span>
            </button>
          ))}
        </div>
      )}

      {/* Events table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center" data-testid="fin-events-empty">
            <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-[13px] text-slate-500">No events match these filters.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-4 py-2.5">Kind</th>
                <th className="text-left px-4 py-2.5">Entity Type</th>
                <th className="text-right px-4 py-2.5">Amount</th>
                <th className="text-left px-4 py-2.5">Actor</th>
                <th className="text-left px-4 py-2.5">Business Date</th>
                <th className="text-left px-4 py-2.5">At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(e => <EventRow key={e.id} e={e}/>)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
